import { ApolloLink, Observable } from '@apollo/client';
import { getOperationAST } from 'graphql';
import { lazyExecuteRequest, lazyUnsubscribe } from 'owa-data-worker-client';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getIsDataWorkerFaulted } from 'owa-config';

let requestCounter = 0;
let headersAreEnumerable: boolean | undefined;

export default function createDataWorkerLink(notEnabledLink: ApolloLink) {
    const link = new ApolloLink((operation, forward) => {
        const op = getOperationAST(operation.query);

        if (
            !isFeatureEnabled('fwk-graphql-worker-link') ||
            !webWorkersSupported() ||
            !getHeadersAreEnumerable() ||
            op?.operation === 'subscription' ||
            getIsDataWorkerFaulted()
        ) {
            // local subscriptions use signalR, which is tightly coupled to the window object
            return notEnabledLink.request(operation, forward);
        } else {
            // try to run the request on the worker.
            return new Observable(outer => {
                const observer = {
                    next: outer.next?.bind(outer),
                    complete: outer.complete?.bind(outer),
                    error: (e: any) => {
                        if (getIsDataWorkerFaulted()) {
                            // if the request fails because the worker didn't initialize, run it on main.
                            notEnabledLink.request(operation, forward)?.subscribe(outer);
                        } else {
                            // otherwise, treat it like a normal error
                            outer.error?.(e);
                        }
                    },
                };

                const requestId = requestCounter++;
                lazyExecuteRequest.importAndExecute(requestId, operation, observer);
                return () => {
                    lazyUnsubscribe.importAndExecute(requestId);
                };
            });
        }
    });

    return link;
}

/**
 *  The RequestOptions object on the graphql context contains a property, headers, which is defined
 *  to be type HeadersWithoutIterator.  The comment in owa-service indicates this is for legacy browsers
 *  that do not support iteration of the headers -- you can only request a header if you know its key.
 *
 *  The problem for the worker/worker-link, with that, is that the Headers object itself is not serializable.
 *  And, to fulfill expecations, we should proxy all headers over.  But, we don't know a priori which headers
 *  will be present.
 *
 *  We can either build up a list of headers that will be proxied or make header enumeration a requirment for
 *  using the data worker.  That requirement is certainly true for monarch.  Older browsers, in web, will still
 *  work, but will be stuck running the graph on the main renderer.
 */
function getHeadersAreEnumerable(): boolean {
    if (headersAreEnumerable === undefined) {
        try {
            const headers = new Headers();
            headers.set('x-test', '1');
            for (const h of headers) {
                h === h;
            }

            headersAreEnumerable = true;
        } catch {
            headersAreEnumerable = false;
        }
    }

    return headersAreEnumerable;
}

function webWorkersSupported(): boolean {
    return typeof Worker !== 'undefined';
}

export function resetStateForTests() {
    headersAreEnumerable = undefined;
}
